import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  addComment,
  addTaskFilter,
  fetchTaskStatuses,
  removeTaskDetailsSidebar,
  removeTaskFilter,
  setTaskDetailsCommentFailed,
  setTaskDetailsCommentPending,
  toggleSidebar,
} from '../../store/tasks.actions';
import { Observable } from 'rxjs';
import {
  getAssignedRoles,
  getAvailabilityOfFilters,
  getDetailsLoading,
  getIsLoading,
  getOwnershipData,
  getSeverityData,
  getShowSidebar,
  getTaskDetails,
  getTaskFilters,
  getTaskStatuses,
  getTimelineData,
  getTaskStatusDistribution,
  selectStakeholderInfo,
  isTasksEmpty,
} from '../../store/tasks.selectors';
import { TaskFilter } from '../../store/tasks.reducer';
import {
  ImplementationTimelineEnum,
  OwnershipEnum,
  SeverityEnum,
  ImplementationTimelineFieldNames,
  ImplementationTimelineLabels,
  OwnershipFieldNames,
  OwnershipLabels,
  SeverityFieldNames,
  SeverityLabels,
  TaskStatusEnum,
  ReferenceTypeEnum,
  RiskStatus,
  PolicyStatus,
  StakeholderRoles,
  StatusDto,
} from '../../../../core/interfaces';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { ValidationService } from '../../../../core/services/validation.service';

@Component({
  templateUrl: './tasks-page.component.html',
  styleUrl: './tasks-page.component.scss',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate(
          '400ms cubic-bezier(0, 0.7, 0.3, 1)',
          style({ transform: 'translateX(0%)' }),
        ),
      ]),
      transition(':leave', [
        animate('300ms linear', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
  standalone: false,
})
export class TasksPageComponent implements OnInit {
  @ViewChild('stakeholdersSection') stakeholdersSection!: ElementRef;
  @ViewChild('referencesSection') referencesSection!: ElementRef;

  highlightSection: string | null = null;
  readonly ImplementationTimelineLabels = ImplementationTimelineLabels;
  readonly SeverityLabels = SeverityLabels;
  readonly OwnershipLabels = OwnershipLabels;
  readonly TaskStatusEnum = TaskStatusEnum;
  readonly ReferenceTypeEnum = ReferenceTypeEnum;
  readonly loadingOwnershipData = Object.values(OwnershipLabels);

  taskFilters$!: Observable<TaskFilter>;
  severityData$!: Observable<ReturnType<typeof getSeverityData>>;
  timelineData$!: Observable<ReturnType<typeof getTimelineData>>;
  ownerShipData$!: Observable<ReturnType<typeof getOwnershipData>>;
  assignedRoles$!: Observable<ReturnType<typeof getAssignedRoles>>;
  getAvailabilityOfFilters$!: Observable<
    ReturnType<typeof getAvailabilityOfFilters>
  >;
  getTaskDetails$!: Observable<ReturnType<typeof getTaskDetails>>;
  stakeholdersInfo$!: Observable<ReturnType<typeof selectStakeholderInfo>>;
  isLoading$!: Observable<boolean>;
  detailsLoading$!: Observable<boolean>;
  showSidebar$!: Observable<boolean>;
  statuses$!: Observable<StatusDto[] | undefined>;
  isTasksEmpty$!: Observable<boolean>;
  statusDistribution$!: Observable<
    ReturnType<typeof getTaskStatusDistribution>
  >;
  commentControl!: FormControl;

  timelineLabels: {
    name: ImplementationTimelineFieldNames;
    value: ImplementationTimelineEnum;
    className: string;
  }[] = [
    {
      name: ImplementationTimelineFieldNames.OVERDUE,
      value: ImplementationTimelineEnum.overdue,
      className: 'status-critical',
    },
    {
      name: ImplementationTimelineFieldNames.IMMEDIATE,
      value: ImplementationTimelineEnum.immediate,
      className: 'status-high',
    },
    {
      name: ImplementationTimelineFieldNames.TWO_WEEKS,
      value: ImplementationTimelineEnum.twoWeeks,
      className: 'status-medium',
    },
    {
      name: ImplementationTimelineFieldNames.NO_RUSH,
      value: ImplementationTimelineEnum.noRush,
      className: 'status-low',
    },
  ];
  severityLabels: {
    name: SeverityFieldNames;
    value: SeverityEnum;
    className: string;
  }[] = [
    {
      name: SeverityFieldNames.CRITICAL,
      value: SeverityEnum.critical,
      className: 'status-critical',
    },
    {
      name: SeverityFieldNames.HIGH,
      value: SeverityEnum.high,
      className: 'status-high',
    },
    {
      name: SeverityFieldNames.MEDIUM,
      value: SeverityEnum.medium,
      className: 'status-medium',
    },
    {
      name: SeverityFieldNames.LOW,
      value: SeverityEnum.low,
      className: 'status-low',
    },
  ];

  ownershipLabels: { name: OwnershipFieldNames; value: OwnershipEnum }[] = [
    {
      name: OwnershipFieldNames.OWNER,
      value: OwnershipEnum.owner,
    },
    {
      name: OwnershipFieldNames.ACCOUNTABLES,
      value: OwnershipEnum.accountable,
    },
    {
      name: OwnershipFieldNames.CONSULTEDS,
      value: OwnershipEnum.contributer,
    },
    {
      name: OwnershipFieldNames.INFORMEDS,
      value: OwnershipEnum.informed,
    },
  ];

  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private validationService: ValidationService,
  ) {}

  ngOnInit(): void {
    this.taskFilters$ = this.store.pipe(select(getTaskFilters));
    this.severityData$ = this.store.pipe(select(getSeverityData));
    this.timelineData$ = this.store.pipe(select(getTimelineData));
    this.ownerShipData$ = this.store.pipe(select(getOwnershipData));
    this.assignedRoles$ = this.store.pipe(select(getAssignedRoles));
    this.statusDistribution$ = this.store.pipe(
      select(getTaskStatusDistribution),
    );
    this.getTaskDetails$ = this.store.pipe(select(getTaskDetails));
    this.stakeholdersInfo$ = this.store.pipe(select(selectStakeholderInfo));
    this.getAvailabilityOfFilters$ = this.store.pipe(
      select(getAvailabilityOfFilters),
    );
    this.isLoading$ = this.store.pipe(select(getIsLoading));
    this.detailsLoading$ = this.store.pipe(select(getDetailsLoading));
    this.showSidebar$ = this.store.pipe(select(getShowSidebar));
    this.statuses$ = this.store.pipe(select(getTaskStatuses));
    this.store.dispatch(fetchTaskStatuses({}));
    this.isTasksEmpty$ = this.store.pipe(select(isTasksEmpty));
    const queryParams = this.activatedRoute.snapshot.queryParams;
    if (Object.keys(queryParams).length) {
      this.onFilterChange(
        { controlId: [] } as unknown as TaskFilter,
        queryParams['fieldName'],
        queryParams['value'],
        queryParams['displayName'],
        false,
      );
    }

    this.commentControl = this.fb.control('', [
      Validators.required,
      Validators.maxLength(1000),
      this.validationService.commentValidation,
    ]);
  }

  onFilterChange(
    filters: TaskFilter,
    filterType: keyof TaskFilter,
    value: any,
    displayName: string,
    isDisabled: boolean,
  ) {
    if (isDisabled) return;
    const isSelected = this.filterSelected(filters, filterType, value);
    if (!isSelected) {
      this.store.dispatch(addTaskFilter({ filterType, value, displayName }));
    } else {
      this.store.dispatch(removeTaskFilter({ filterType, value }));
    }
  }

  filterSelected(filters: TaskFilter, filterName: any, value: any) {
    const filter = filters[filterName as keyof TaskFilter] as any;
    return filter.includes(value);
  }

  hideSidebar() {
    this.store.dispatch(removeTaskDetailsSidebar());
    this.store.dispatch(toggleSidebar({ showSidebar: false }));
  }

  addComment(taskId: string, comment: string) {
    if (this.commentControl.invalid) {
      this.commentControl.markAsTouched();
      return;
    }
    const commentText = (comment ?? '').trim();
    if (!commentText) {
      this.commentControl.markAsTouched();
      return;
    }
    this.store.dispatch(setTaskDetailsCommentPending({ pending: true }));
    this.store.dispatch(addComment({ commentText, taskId }));
    this.commentControl.patchValue('');
  }

  hideFailedComment() {
    this.store.dispatch(setTaskDetailsCommentFailed({ failed: false }));
  }
  scrollToSection(section: 'stakeholders' | 'references') {
    const element =
      section === 'stakeholders'
        ? this.stakeholdersSection
        : this.referencesSection;

    if (element) {
      element.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Wait for scroll to complete (approximately)
      setTimeout(() => {
        this.highlightSection = section;

        // Remove highlight after animation completes
        setTimeout(() => {
          this.highlightSection = null;
        }, 600);
      }, 500);
    }
  }

  ngOnDestroy() {
    this.store.dispatch(toggleSidebar({ showSidebar: false }));
  }

  protected readonly ImplementationTimelineEnum = ImplementationTimelineEnum;
  protected readonly RiskStatus = RiskStatus;
  protected readonly PolicyStatus = PolicyStatus;
  protected readonly Object = Object;
  protected readonly StakeholderRoles = StakeholderRoles;
  protected readonly JSON = JSON;
}
